import React, { useLayoutEffect, useState } from "react";
import classNames from "classnames";
import { WEBSITE_BUILDER_Z_LAYERS } from "pages/secure/facility/websiteBuilder/editor/utility/websiteBuilderZLayers";
import Portal from "elements/Portal";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { TableBuilderDropdown, TTableBuilderDropdownOptionSections } from "../dropdown/TableBuilderDropdown";
import { useTableBuilderContext } from "../../TableBuilderContext";
import "./tableBuilderOption.scss";
import { IconDefinition } from "@fortawesome/pro-regular-svg-icons";
import { Placement } from "react-bootstrap/esm/Overlay";

export interface ITableBuilderOptionProps {
  cellId: string;
  draggable: boolean;
  isDragging: boolean;
  icon: IconDefinition;
  iconClassName: string;
  optionSections: TTableBuilderDropdownOptionSections;
  tooltipPlacement: Placement;
  tooltipContent: JSX.Element;
  dropdownOpen: boolean;
  containerRef: React.MutableRefObject<HTMLDivElement>;
  toggleDropdown: () => void;
  dropdownRegistration?: (div: HTMLDivElement) => void;
}

interface ITableBuilderOptionPosition {
  x: number;
  y: number;
}

const OPTION_DRAGGING_OPACITY = 0;

export function TableBuilderOption(props: ITableBuilderOptionProps) {
  const {
    cellId,
    draggable,
    isDragging,
    icon,
    iconClassName,
    optionSections,
    tooltipPlacement,
    tooltipContent,
    dropdownOpen,
    toggleDropdown,
    dropdownRegistration,
    containerRef,
  } = props;

  const { containerBuilderUIPortalId } = useTableBuilderContext();
  
  const [position, setPosition] = useState<ITableBuilderOptionPosition>(null);
  
  const hideTooltip = dropdownOpen || isDragging;

  useLayoutEffect(() => {
    const portal = document.getElementById(containerBuilderUIPortalId);

    if (containerRef.current == null || portal == null) {
      return;
    }

    const boundingRect = containerRef.current.getBoundingClientRect();
    const portalBoundingRect = portal.getBoundingClientRect();

    setPosition({
      x: boundingRect.x - portalBoundingRect.x,
      y: boundingRect.y - portalBoundingRect.y,
    });
  }, [containerBuilderUIPortalId]);

  return (
    <Portal isMounted={true} parentId={containerBuilderUIPortalId}>
      <div
        className="table-builder-option"
        ref={el => {
          if (dropdownRegistration != null) {
            dropdownRegistration(el);
          }
        }}
        style={{
          left: position?.x,
          top: position?.y,
          visibility: position == null ? "hidden" : "visible",
          opacity: isDragging ? OPTION_DRAGGING_OPACITY : null,
          zIndex: WEBSITE_BUILDER_Z_LAYERS.designSpace.containerBuilderUI,
        }}
      >
        <TableBuilderDropdown
          alignment="left"
          open={dropdownOpen}
          sections={optionSections}
          toggleDropdown={toggleDropdown}
        >
          <OverlayTrigger
            placement={tooltipPlacement}
            show={hideTooltip ? false : undefined}
            delay={{
              show: 500,
              hide: 100,
            }}
            overlay={
              <Tooltip id={`table-builder-option-${cellId}`}>
                {tooltipContent}
              </Tooltip>
            }
          >
            <FontAwesomeIcon
              className={classNames("table-builder-option-icon", iconClassName)}
              style={{
                cursor: !draggable ? "pointer" : isDragging ? "grabbing" : "grab",
              }}
              icon={icon}
            />
          </OverlayTrigger>
        </TableBuilderDropdown>
      </div>
    </Portal>
  );
}